import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";

import HttpManager from "../../../services/HttpManager";
import { KitFicha, KitsCatalogo } from "../../../services/v2/prospects.service";

/**
 * Botón Kits (Esteban, 2026-11-01): elegir el curso y mandarle al cliente la ficha de cada kit tal como se vende en la Tienda.
 * En Honduras las fichas son las fotos de los kits del catálogo; una ficha que no cuadra con el precio de la Tienda sale apagada
 * («Por actualizar») y no se puede mandar. En México, por ahora, las páginas de kits de los catálogos de Karen.
 */

/** Las fichas ya bajadas en esta sesión (blob URLs): abrir el modal otra vez no las vuelve a pedir. */
const fotos = new Map<string, string>();

const Ficha: React.FC<{ campusId: number; kitId: string; grande?: boolean; alt: string }> = ({ campusId, kitId, grande, alt }) => {
	const clave = kitId + (grande ? ":g" : ":m");
	const [url, setUrl] = useState<string | null>(fotos.get(clave) || null);
	const [falla, setFalla] = useState(false);
	useEffect(() => {
		if (fotos.get(clave)) { setUrl(fotos.get(clave)!); return; }
		let vivo = true;
		HttpManager.getInstance().prospectsService.kitFoto(campusId, kitId, !grande)
			.then((r) => { const u = URL.createObjectURL(r.data); fotos.set(clave, u); if (vivo) setUrl(u); })
			.catch(() => { if (vivo) setFalla(true); });
		return () => { vivo = false; };
	}, [clave]);
	if (falla) return <div className="km-foto vacia">Sin ficha</div>;
	return url ? <img className="km-foto" src={url} alt={alt} draggable={false} /> : <div className="km-foto cargando" aria-label="Cargando ficha" />;
};

const IcoLupa = () => <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path fill="currentColor" d="M10 2a8 8 0 0 1 6.32 12.9l5.39 5.4-1.41 1.4-5.4-5.39A8 8 0 1 1 10 2zm0 2a6 6 0 1 0 0 12 6 6 0 0 0 0-12z" /></svg>;

interface Props {
	campusId: number;
	prospectId: number;
	canalId: number | null;
	puedeEnviar: boolean;
	onEnviado: () => void;
	onClose: () => void;
}

const KitsModal: React.FC<Props> = ({ campusId, prospectId, canalId, puedeEnviar, onEnviado, onClose }) => {
	const http = HttpManager.getInstance();
	const [datos, setDatos] = useState<KitsCatalogo | null>(null);
	const [curso, setCurso] = useState<string | null>(null);
	const [marcados, setMarcados] = useState<string[]>([]);
	const [grande, setGrande] = useState<KitFicha | null>(null);
	const [texto, setTexto] = useState("");
	const [ocupado, setOcupado] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const chips = useRef<HTMLDivElement>(null);

	useEffect(() => {
		http.prospectsService.kits(campusId, prospectId).then((r) => {
			setDatos(r.data);
			const primero = r.data.sugerido || r.data.cursos[0]?.code || null;
			elegirCurso(primero, r.data);
		}).catch((e) => setError(e?.response?.data?.message || "No se pudieron cargar los kits."));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, prospectId]);

	// El curso que le interesa al cliente va primero.
	const cursos = useMemo(() => {
		const l = datos?.cursos || [];
		const s = datos?.sugerido;
		return s ? [...l.filter((c) => c.code === s), ...l.filter((c) => c.code !== s)] : l;
	}, [datos]);
	const actual = cursos.find((c) => c.code === curso) || null;
	const listos = (actual?.kits || []).filter((k) => k.estado === "lista");

	function elegirCurso(code: string | null, d: KitsCatalogo | null = datos) {
		setCurso(code);
		const c = d?.cursos.find((x) => x.code === code);
		setMarcados((c?.kits || []).filter((k) => k.estado === "lista").map((k) => k.id));
		setError(null);
	}
	const alternar = (k: KitFicha) => {
		if (k.estado !== "lista") return;
		setMarcados((m) => (m.includes(k.id) ? m.filter((x) => x !== k.id) : [...m, k.id]));
	};
	const enviar = async () => {
		// Se mandan en el orden del catálogo (del más básico al más completo), no en el orden en que se tocaron.
		const ids = (actual?.kits || []).filter((k) => marcados.includes(k.id)).map((k) => k.id);
		setOcupado(true); setError(null);
		try {
			const r = await http.prospectsService.enviarKits(campusId, prospectId, ids, canalId, texto);
			onEnviado();
			if (r.data.error) setError(r.data.error); else onClose();
		} catch (e: any) { setError(e?.response?.data?.message || "No se pudieron enviar los kits."); }
		finally { setOcupado(false); }
	};

	const n = marcados.length;
	return (
		<Modal show onHide={ocupado ? undefined : onClose} size="xl" className="ventas-nuevo-modal km-modal" fullscreen="md-down" scrollable>
			<Modal.Header closeButton={!ocupado}>
				<Modal.Title style={{ fontFamily: "Bricolage, sans-serif", fontWeight: 800 }}>{grande ? grande.titulo : "Enviar Kits"}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{grande ? (
					<div className="km-grande">
						<Ficha campusId={campusId} kitId={grande.id} grande alt={grande.titulo} />
					</div>
				) : (
					<>
						{error ? <div className="aviso error">{error}</div> : null}
						{!datos && !error ? <div className="apagado">Cargando…</div> : null}
						{datos && !cursos.length ? <div className="km-vacio">Este plantel todavía no tiene kits con ficha en la Tienda.</div> : null}
						{cursos.length ? (
							<div className="km-cursos" ref={chips} role="tablist" aria-label="Curso">
								{cursos.map((c) => (
									<button key={c.code} type="button" role="tab" aria-selected={c.code === curso} className={"km-chip" + (c.code === curso ? " on" : "")}
										onClick={() => elegirCurso(c.code)}>
										{c.name}
										{c.code === datos?.sugerido ? <span className="km-interes">Le interesa</span> : null}
									</button>
								))}
							</div>
						) : null}
						{actual ? (
							<div className="km-grid">
								{actual.kits.map((k) => {
									const on = marcados.includes(k.id);
									const lista = k.estado === "lista";
									const agotado = k.estado === "agotado";
									return (
										<div key={k.id} className={"km-kit" + (on ? " on" : "") + (lista ? "" : agotado ? " agotada" : " apagada")}>
											<button type="button" className="km-tocar" onClick={() => alternar(k)} aria-pressed={on} disabled={!lista}
												aria-label={(on ? "Quitar " : "Elegir ") + k.titulo}>
												<Ficha campusId={campusId} kitId={k.id} alt={k.titulo} />
												{agotado ? <span className="km-agotado">Agotado</span> : <span className="hm-check km-check" aria-hidden="true">{on ? "✓" : ""}</span>}
											</button>
											<div className="km-pie">
												<span className="km-tx">
													<b>{k.titulo}</b>
													{lista ? (k.precioTexto ? <span className="km-precio">{k.precioTexto}</span> : null)
														: agotado ? <span className="km-motivo">Por ahora no se vende</span>
														: <span className="km-motivo">{k.estado === "revisando" ? "Revisando ficha…" : "Por actualizar · " + (k.motivo || "")}</span>}
												</span>
												<button type="button" className="km-lupa" onClick={() => setGrande(k)} aria-label={"Ver " + k.titulo + " en grande"} title="Ver en grande"><IcoLupa /></button>
											</div>
										</div>
									);
								})}
							</div>
						) : null}
						{actual && listos.length ? (
							<label className="km-texto">
								<span>Mensaje (opcional)</span>
								<textarea rows={2} value={texto} maxLength={1024} onChange={(e) => setTexto(e.target.value)} disabled={ocupado}
									placeholder="Le comparto los kits del curso:" />
							</label>
						) : null}
					</>
				)}
			</Modal.Body>
			<Modal.Footer className="hm-pie">
				{grande ? (
					<>
						<button className="btn-wa" onClick={() => setGrande(null)}>Volver</button>
						{grande.estado === "lista" ? (
							<button className={"btn-wa" + (marcados.includes(grande.id) ? "" : " pri")} onClick={() => { alternar(grande); setGrande(null); }}>
								{marcados.includes(grande.id) ? "Quitar" : "Elegir"}
							</button>
						) : null}
					</>
				) : (
					<>
						{listos.length > 1 ? (
							<button className="btn-wa km-todos" disabled={ocupado}
								onClick={() => setMarcados(n === listos.length ? [] : listos.map((k) => k.id))}>{n === listos.length ? "Quitar todos" : "Elegir todos"}</button>
						) : null}
						<button className="btn-wa pri hm-enviar" disabled={!n || !puedeEnviar || ocupado} onClick={enviar}
							title={puedeEnviar ? "" : "Pasaron 24 h desde su último mensaje: solo plantilla"}>
							{ocupado ? "Enviando…" : n > 1 ? "Enviar " + n + " kits" : "Enviar kit"}
						</button>
					</>
				)}
			</Modal.Footer>
		</Modal>
	);
};

export default KitsModal;
